<template>
  <!--
    NZSC-2859 법인등록번호로 조회하기 모달.
    Figma 실측 기반 (웹 11525-12365 / 11526-12405, 모바일 11526-14013 / 11526-13985).

    bizno gb=2 역조회는 1건만 반환되지만 UI 일관성을 위해 리스트 형태로 보여준다.
    items.length === 1 이라도 라디오 리스트로 렌더 (디자이너 요구: 법인번호 모달도 동일 구조).

    placeholder: 하이픈 없이 "0000000000" (Figma 모바일 11526:13860). 내부 상태는 문자열로 유지.
    - 웹은 6자리 + 7자리 별도 표시도 가능하나 Figma 실측상 웹 모달도 일체형 input 이다
      (11526:12419 에 "110111-1234567" 한 줄로). 여기선 일체형으로 통일.
  -->
  <div
    class="corp_modal_overlay"
    @click.self="fnClose"
  >
    <div class="corp_modal">
      <!-- 모바일 타이틀바 -->
      <div class="corp_modal_mobile_titlebar">
        <img
          class="corp_modal_mobile_back"
          src="@/assets/images/sherlock/icn/arrow_left.svg"
          alt="뒤로"
          @click="fnClose"
        />
        <p class="corp_modal_mobile_title">법인등록번호로 조회하기</p>
      </div>

      <div class="corp_modal_body">
        <!-- 웹 헤더 -->
        <div class="corp_modal_web_header">
          <p class="corp_modal_title">법인 등록 번호로 조회하기</p>
          <button
            type="button"
            class="corp_modal_close_btn"
            aria-label="닫기"
            @click="fnClose"
          >
            ×
          </button>
        </div>

        <!-- 검색 입력줄 -->
        <div class="corp_modal_search_row">
          <input
            ref="keywordInput"
            type="text"
            class="corp_modal_search_input"
            :placeholder="inputPlaceholder"
            v-model="keyword"
            @input="fnOnKeywordInput"
            @keydown.enter="fnSearchManual"
            :disabled="autocompleteDisabled"
            maxlength="14"
            inputmode="numeric"
          />
          <!-- 웹: 파란 [검색하기] 버튼 -->
          <button
            class="corp_modal_search_btn"
            type="button"
            @click="fnSearchManual"
            :disabled="autocompleteDisabled"
          >
            검색하기
          </button>
          <!-- 모바일: 우측 돋보기 아이콘 버튼 -->
          <button
            class="corp_modal_search_btn_mobile"
            type="button"
            aria-label="검색"
            @click="fnSearchManual"
            :disabled="autocompleteDisabled"
          >
            <img
              src="@/assets/images/sherlock/icn/magnifier.svg"
              alt="검색"
            />
          </button>
        </div>

        <!-- 결과 영역 -->
        <div class="corp_modal_result_box">
          <div
            v-if="isLoading"
            class="corp_modal_placeholder"
          >
            <div class="corp_modal_spinner"></div>
            <p class="corp_modal_placeholder_text">검색 중...</p>
          </div>

          <div
            v-else-if="quotaError"
            class="corp_modal_placeholder"
          >
            <img
              class="corp_modal_warn_icon"
              src="@/assets/images/sherlock/icn/warn.svg"
              alt="안내"
            />
            <p class="corp_modal_placeholder_text">자동완성을 일시적으로 이용할 수 없습니다</p>
            <p class="corp_modal_placeholder_sub">법인명과 법인등록번호를 직접 입력해 주세요</p>
          </div>

          <div
            v-else-if="networkError"
            class="corp_modal_placeholder"
          >
            <img
              class="corp_modal_warn_icon"
              src="@/assets/images/sherlock/icn/warn.svg"
              alt="에러"
            />
            <p class="corp_modal_placeholder_text">검색에 실패했어요</p>
            <p class="corp_modal_placeholder_sub">
              잠시 후 다시 시도하거나 다른 방법으로 조회해 주세요
            </p>
          </div>

          <div
            v-else-if="!hasSearched"
            class="corp_modal_placeholder corp_modal_placeholder_empty"
          ></div>

          <!-- 결과없음: 대안경로 = 법인명으로 조회하기 -->
          <div
            v-else-if="items.length === 0"
            class="corp_modal_placeholder"
          >
            <img
              class="corp_modal_warn_icon"
              src="@/assets/images/sherlock/icn/warn.svg"
              alt="결과없음"
            />
            <p class="corp_modal_placeholder_text">
              입력하신 법인 등록 번호와 일치하는 법인 정보를 찾을 수 없습니다.
            </p>
            <p class="corp_modal_placeholder_sub">
              정보를 다시 확인하시거나, 법인명으로 조회를 진행해 보시기 바랍니다.
            </p>
            <button
              type="button"
              class="corp_modal_alt_btn"
              @click="fnSwitchToName"
            >
              법인명으로 조회하기
            </button>
          </div>

          <!-- 결과 리스트 (1건이라도 리스트로 렌더 — 디자인 일관성) -->
          <ul
            v-else
            class="corp_modal_list"
          >
            <li
              v-for="(item, idx) in items"
              :key="`${item.cno}-${idx}`"
              class="corp_modal_list_item"
              :class="{ selected: selectedIdx === idx }"
              @click="fnSelectItem(idx)"
            >
              <img
                class="corp_modal_radio"
                :src="
                  selectedIdx === idx
                    ? require('@/assets/images/sherlock/icn/radio_selected.svg')
                    : require('@/assets/images/sherlock/icn/radio_disabled.svg')
                "
                alt="선택"
              />
              <div class="corp_modal_list_item_text">
                <span class="corp_modal_company_name">{{ item.company }}</span>
                <span class="corp_modal_company_cno">{{ fnFormatCno(item.cno) }}</span>
              </div>
            </li>
          </ul>
        </div>

        <!-- 하단 [조회하기] -->
        <div class="corp_modal_footer">
          <button
            type="button"
            class="corp_modal_confirm_btn"
            :disabled="selectedIdx === null"
            @click="fnConfirm"
          >
            조회하기
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { formatCno } from "@/utils/corpCno";

const QUOTA_DISABLED_KEY = "sherlock_corp_autocomplete_disabled";

export default {
  name: "SherlockCorpRegnoSearchModalComponent",
  props: {
    initialKeyword: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      keyword: "",
      isLoading: false,
      items: [],
      hasSearched: false,
      selectedIdx: null,
      quotaError: false,
      networkError: false,
      autocompleteDisabled: false,
      requestSeq: 0,
    };
  },
  computed: {
    inputPlaceholder() {
      return this.quotaError ? "법인등록번호를 직접 입력해 주세요" : "000000-0000000";
    },
  },
  created() {
    this.$mixpanel_track("View Sherlock-Search-Corp-RegnoSearchModal");
    if (sessionStorage.getItem(QUOTA_DISABLED_KEY) === "1") {
      this.quotaError = true;
      this.autocompleteDisabled = true;
    }
  },
  mounted() {
    this.fnInit();
  },
  methods: {
    ...mapActions("sherlockStore/corpStore", ["acPostSherlockCorpRegnoLookup"]),
    fnInit() {
      if (this.initialKeyword) {
        this.keyword = this.initialKeyword;
        this.fnSearch();
      }
      this.$nextTick(() => {
        if (this.$refs.keywordInput) this.$refs.keywordInput.focus();
      });
    },
    /**
     * 숫자와 하이픈만 허용 (사용자가 하이픈 넣든 말든 서버는 정규화함).
     */
    fnOnKeywordInput() {
      // 숫자와 하이픈만 남김
      const filtered = (this.keyword || "").replace(/[^0-9-]/g, "");
      if (filtered !== this.keyword) {
        this.keyword = filtered;
      }
    },
    fnSearchManual() {
      this.fnSearch();
    },
    async fnSearch() {
      this.networkError = false;
      const kw = (this.keyword || "").trim();
      const digits = kw.replace(/\D/g, "");
      if (!digits) {
        this.items = [];
        this.hasSearched = false;
        this.selectedIdx = null;
        return;
      }
      if (digits.length < 13) {
        // 13자리 숫자 완성되기 전엔 안 때림 (서버 400 방지)
        this.items = [];
        this.hasSearched = false;
        this.selectedIdx = null;
        return;
      }
      if (this.autocompleteDisabled) return;

      const seq = ++this.requestSeq;
      this.isLoading = true;
      try {
        const res = await this.acPostSherlockCorpRegnoLookup({ cno: kw });
        if (seq !== this.requestSeq) return;

        if (!res) {
          this.items = [];
          this.hasSearched = true;
          this.selectedIdx = null;
          return;
        }
        if (res.errorType === "QUOTA_EXCEEDED") {
          this.quotaError = true;
          this.autocompleteDisabled = true;
          sessionStorage.setItem(QUOTA_DISABLED_KEY, "1");
          this.items = [];
          this.hasSearched = false;
          this.selectedIdx = null;
          return;
        }
        if (res.errorType === "NETWORK_ERROR" || res.errorType === "UNKNOWN_ERROR") {
          this.networkError = true;
          this.items = [];
          this.hasSearched = false;
          this.selectedIdx = null;
          return;
        }
        if (res.errorType === "INVALID_PARAM") {
          this.items = [];
          this.hasSearched = false;
          this.selectedIdx = null;
          return;
        }

        // 역조회 응답: { item: {company, cno, bno, ...} | null }
        // UI 일관성 유지 위해 단건이라도 배열로 래핑
        const item = res.item;
        this.items = item ? [item] : [];
        this.hasSearched = true;
        this.selectedIdx = null;
      } finally {
        if (seq === this.requestSeq) this.isLoading = false;
      }
    },
    fnSelectItem(idx) {
      this.selectedIdx = idx;
    },
    fnConfirm() {
      if (this.selectedIdx === null) return;
      const chosen = this.items[this.selectedIdx];
      if (!chosen) return;
      this.$mixpanel_track("Click Sherlock-Search-Corp-RegnoSearchModal-Confirm");
      this.$emit("childFnSelectCorp", {
        company: chosen.company,
        cno: chosen.cno,
        bno: chosen.bno,
      });
    },
    fnSwitchToName() {
      this.$mixpanel_track("Click Sherlock-Search-Corp-RegnoSearchModal-SwitchToName");
      this.$emit("childFnSwitchToName");
    },
    fnClose() {
      this.$emit("childFnClose");
    },
    fnFormatCno(cno) {
      return formatCno(cno);
    },
  },
};
</script>

<style scoped>
/* NZSC-2859: 법인명 모달과 완전 동일한 스타일 체계 유지 (중복 제거하면 단일 cue로 엮기 어려움 — 모달 독립 유지) */
.corp_modal_overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.corp_modal {
  background: #fff;
  border-radius: 10px;
  width: 760px;
  max-width: 92vw;
  max-height: 90vh;
  padding: 30px 30px 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.corp_modal_mobile_titlebar {
  display: none;
}

.corp_modal_body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

.corp_modal_web_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.corp_modal_title {
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: #121212;
  margin: 0;
  text-align: left;
}
.corp_modal_close_btn {
  background: transparent;
  border: none;
  font-size: 26px;
  line-height: 1;
  color: #121212;
  cursor: pointer;
  padding: 0 4px;
}

.corp_modal_search_row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.corp_modal_search_input {
  flex: 1;
  height: 44px;
  border-radius: 6px;
  border: 1.2px solid #3191ff;
  background: #fff;
  padding: 10px 12px;
  font-size: 15px;
  line-height: 24px;
  color: #121212;
  box-sizing: border-box;
  outline: none;
}
.corp_modal_search_input::placeholder {
  color: #9aa6ac;
}
.corp_modal_search_input:disabled {
  background: #f4f4f4;
  border-color: #dde2e4;
  color: #979797;
}

.corp_modal_search_btn {
  min-width: 150px;
  height: 44px;
  border-radius: 6px;
  background: #3191ff;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
}
.corp_modal_search_btn:disabled {
  background: #c0c0c0;
  cursor: not-allowed;
}
.corp_modal_search_btn_mobile {
  display: none;
}

.corp_modal_result_box {
  border: 1px solid #dde2e4;
  border-radius: 6px;
  min-height: 320px;
  max-height: 440px;
  overflow-y: auto;
  background: #fff;
  flex: 1 1 auto;
}

.corp_modal_placeholder {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  box-sizing: border-box;
}
.corp_modal_placeholder_empty {
  gap: 0;
}
.corp_modal_placeholder_text {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #4c5256;
  text-align: center;
  line-height: 26px;
}
.corp_modal_placeholder_sub {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  color: #606060;
  text-align: center;
  line-height: 24px;
}
.corp_modal_warn_icon {
  width: 40px;
  height: 40px;
  margin-bottom: 4px;
}

.corp_modal_spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8ec;
  border-top-color: #3191ff;
  border-radius: 50%;
  animation: corpModalSpin 0.8s linear infinite;
}
@keyframes corpModalSpin {
  to {
    transform: rotate(360deg);
  }
}

.corp_modal_alt_btn {
  margin-top: 12px;
  background: #3191ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  min-height: 38px;
}
.corp_modal_alt_btn:hover {
  background: #2375cb;
}

.corp_modal_list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.corp_modal_list_item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.1s;
}
.corp_modal_list_item:last-child {
  border-bottom: none;
}
.corp_modal_list_item:hover {
  background: #f8fbff;
}
.corp_modal_list_item.selected {
  background: #f5faff;
}
.corp_modal_radio {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
.corp_modal_list_item_text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.corp_modal_company_name {
  font-size: 15px;
  font-weight: 500;
  color: #121212;
  line-height: 24px;
  word-break: break-all;
}
.corp_modal_company_cno {
  font-size: 13px;
  font-weight: 400;
  color: #606060;
  line-height: 18px;
}

.corp_modal_footer {
  display: flex;
  justify-content: center;
  padding-top: 6px;
}
.corp_modal_confirm_btn {
  width: 300px;
  height: 44px;
  border-radius: 6px;
  background: #3191ff;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
}
.corp_modal_confirm_btn:disabled {
  background: #f4f4f4;
  color: #52585b;
  cursor: not-allowed;
}

@media (max-width: 650px) {
  .corp_modal_overlay {
    background: #f9f9f9;
    align-items: stretch;
    justify-content: stretch;
  }
  .corp_modal {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
    padding: 0;
    background: #f9f9f9;
  }
  .corp_modal_mobile_titlebar {
    display: flex;
    align-items: center;
    height: 60px;
    padding: 0 20px;
    background: #fff;
    border-bottom: 1px solid #edf0f3;
    position: relative;
    flex-shrink: 0;
  }
  .corp_modal_mobile_back {
    width: 24px;
    height: 24px;
    cursor: pointer;
  }
  .corp_modal_mobile_title {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-size: 16px;
    font-weight: 500;
    color: #121212;
    margin: 0;
    line-height: 24px;
  }
  .corp_modal_body {
    padding: 20px;
    gap: 16px;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }
  .corp_modal_web_header {
    display: none;
  }
  .corp_modal_search_row {
    gap: 0;
    position: relative;
    align-items: center;
  }
  .corp_modal_search_input {
    background: #f1f3f4;
    border-color: transparent;
    height: 44px;
    font-size: 14px;
    padding-right: 44px;
  }
  .corp_modal_search_input:focus {
    border-color: #3191ff;
    background: #fff;
  }
  .corp_modal_search_btn {
    display: none;
  }
  .corp_modal_search_btn_mobile {
    display: flex;
    position: absolute;
    right: 0;
    top: 0;
    width: 44px;
    height: 44px;
    background: transparent;
    border: none;
    cursor: pointer;
    align-items: center;
    justify-content: center;
  }
  .corp_modal_search_btn_mobile img {
    width: 20px;
    height: 20px;
  }
  .corp_modal_result_box {
    flex: 1;
    height: auto;
    min-height: 0;
    max-height: none;
    border: none;
    border-radius: 0;
    background: transparent;
  }
  .corp_modal_list {
    background: #fff;
    border-radius: 6px;
    overflow: hidden;
  }
  .corp_modal_list_item {
    padding: 20px;
  }
  .corp_modal_company_name {
    font-size: 14px;
    line-height: 20px;
  }
  .corp_modal_placeholder_text {
    font-size: 15px;
    line-height: 24px;
  }
  .corp_modal_placeholder_sub {
    font-size: 13px;
    line-height: 18px;
    color: #4c5256;
  }
  .corp_modal_footer {
    padding: 16px 0 20px;
    flex-shrink: 0;
  }
  .corp_modal_confirm_btn {
    width: 100%;
    height: 44px;
    font-size: 13px;
  }
  .corp_modal_confirm_btn:disabled {
    background: #dfdfdf;
    color: #979797;
  }
  .corp_modal_alt_btn {
    font-size: 12px;
    padding: 10px 12px;
  }
}
</style>
